import React, { useRef } from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction, TextLink } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { useFocus, useHover } from '../../../hooks/useElementState';
import { applyComponentTheme } from '../../../theme';
import { PrimaryCta, SecondaryCta } from '../../Cta';
import Icon from '../../Icon';
import {
  CtaWrapper,
  Heading,
  InfoCardContainer,
  InfoCardRichText,
  InfoCardWrapper,
  LogoImage,
  StyledImage,
  StyledLink,
  TextContainer,
} from './styles';

export type InfoCardProps = {
  headerIcon?: string;
  heading: string;
  body: string;
  /** @deprecated */
  cta?: CallToAction;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
  link?: TextLink & { icon?: string };
  logo?: Image;
  image?: Image;
  useHeadingAsH4?: boolean;
  displayBackground?: boolean;
  alignLeft?: boolean;
} & ComponentProps;

export const InfoCardContent: React.FC<InfoCardProps> = ({
  headerIcon,
  heading,
  body,
  cta,
  primaryCta,
  secondaryCta,
  link,
  logo,
  useHeadingAsH4,
  alignLeft = true,
}) => {
  // Default to secondary for backwards compatibility
  const secondaryCtaOrCta = secondaryCta || cta;

  const primaryCtaRef = useRef<HTMLAnchorElement>(null);
  const secondaryCtaRef = useRef<HTMLAnchorElement>(null);
  const isHoveringOnPrimaryCta = useHover(primaryCtaRef);
  const isHoveringOnSecondaryCta = useHover(secondaryCtaRef);
  const isFocusedOnPrimaryCta = useFocus(primaryCtaRef);
  const isFocusedOnSecondaryCta = useFocus(secondaryCtaRef);

  const displayIconVariant =
    (primaryCta && !(isHoveringOnPrimaryCta || isFocusedOnPrimaryCta)) ||
    (secondaryCtaOrCta && !(isHoveringOnSecondaryCta || isFocusedOnSecondaryCta));

  return (
    <>
      <TextContainer alignLeft={alignLeft}>
        <Heading alignLeft={alignLeft} as={useHeadingAsH4 ? 'h4' : 'h3'}>
          {headerIcon && (
            <Icon name={headerIcon} size="large" displayVariant={displayIconVariant} />
          )}
          <span>{heading}</span>
        </Heading>
        {logo && <LogoImage src={logo.url} alt={logo.altText || ''} />}
        <InfoCardRichText html={body} alignLeft={alignLeft} />
      </TextContainer>
      {primaryCta && (
        <CtaWrapper>
          <PrimaryCta cta={primaryCta} innerRef={primaryCtaRef} />
        </CtaWrapper>
      )}
      {secondaryCtaOrCta && (
        <CtaWrapper>
          <SecondaryCta cta={secondaryCtaOrCta} innerRef={secondaryCtaRef} />
        </CtaWrapper>
      )}
      {link && (
        <StyledLink href={link.url} openInNewTab={link.openInNewTab}>
          {link.text}
          {link.icon && <Icon name={link.icon} size="small" />}
        </StyledLink>
      )}
    </>
  );
};

const InfoCard: React.FC<InfoCardProps> = ({
  displayBackground,
  alignLeft = true,
  image,
  ...props
}) => (
  <InfoCardWrapper tabIndex={0} {...componentProps(props)}>
    {image && <StyledImage image={image} focusable={false} />}
    <InfoCardContainer
      displayBackground={displayBackground}
      hasImage={!!image}
      alignLeft={alignLeft}>
      <InfoCardContent {...props} alignLeft={alignLeft} />
    </InfoCardContainer>
  </InfoCardWrapper>
);

export default applyComponentTheme(InfoCard, 'InfoCard');
